import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { C, S } from '../../../utils/styles';
import Icon from '../../Icon';
import { useToast } from '../../Toast';
import {
  getCourse,
  updateCourse,
  publishCourse,
  unpublishCourse,
  AUDIENCE_OPTIONS,
} from './api';
import { renderMarkdown } from './markdown';

const BLOCK_TYPES = [
  { value: 'richText',           label: 'Rich Text (Markdown)', icon: 'document-text-outline' },
  { value: 'image',              label: 'Image',                icon: 'image-outline' },
  { value: 'videoEmbed',         label: 'Video Embed',          icon: 'videocam-outline' },
  { value: 'attachment',         label: 'Attachment Link',      icon: 'attach-outline' },
  { value: 'callout',            label: 'Callout',              icon: 'information-circle-outline' },
  { value: 'keyTakeaway',        label: 'Key Takeaway',         icon: 'star-outline' },
  { value: 'definitionTooltip',  label: 'Definition',           icon: 'help-circle-outline' },
  { value: 'reflectionPrompt',   label: 'Reflection Prompt',    icon: 'chatbubble-ellipses-outline' },
];

const CALLOUT_VARIANTS = [
  { value: 'info',    label: 'Info',    color: C.accent,  bg: C.accentBg },
  { value: 'success', label: 'Success', color: C.green,   bg: C.greenLight },
  { value: 'warn',    label: 'Warning', color: C.orange,  bg: C.orangeLight },
];

function tempId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

export default function CourseBuilder({ courseId, onBack }) {
  const showToast = useToast();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [activeTab, setActiveTab] = useState('metadata');
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCourse(courseId);
      setCourse(data);
      const firstLesson = (data.lessons || [])[0];
      setActiveLessonId(firstLesson ? firstLesson.id : null);
      setDirty(false);
    } catch (err) {
      showToast(err.message || 'Failed to load course', 'error');
    } finally {
      setLoading(false);
    }
  }, [courseId, showToast]);

  useEffect(() => { load(); }, [load]);

  const updateLocal = (patch) => {
    setCourse((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
  };

  const updateLessons = (mapper) => {
    setCourse((prev) => {
      if (!prev) return prev;
      const next = (prev.lessons || []).map(mapper).filter(Boolean);
      return { ...prev, lessons: next };
    });
    setDirty(true);
  };

  const updateQuiz = (patch) => {
    setCourse((prev) => prev ? { ...prev, quiz: { ...(prev.quiz || {}), ...patch } } : prev);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!course) return;
    setSaving(true);
    try {
      const payload = {
        title: course.title,
        description: course.description || '',
        audience: course.audience,
        mustCompleteBeforeAccess: !!course.mustCompleteBeforeAccess,
        estimatedMinutes: course.estimatedMinutes ?? null,
        lessons: course.lessons || [],
        quiz: course.quiz || {},
      };
      const fresh = await updateCourse(course.id, payload);
      setCourse(fresh);
      setDirty(false);
      showToast('Course saved', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to save course', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!course) return;
    if (dirty) {
      const ok = window.confirm('You have unsaved changes. Save and publish?');
      if (!ok) return;
      await handleSave();
    }
    setPublishing(true);
    try {
      const fresh = await publishCourse(course.id);
      setCourse(fresh);
      showToast(`Course published (v${fresh.version})`, 'success');
    } catch (err) {
      const detail = err.errors && err.errors.length ? `\n• ${err.errors.join('\n• ')}` : '';
      showToast(`Cannot publish: ${err.message}${detail}`, 'error');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!course) return;
    try {
      const fresh = await unpublishCourse(course.id);
      setCourse(fresh);
      showToast('Course reverted to draft', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to unpublish', 'error');
    }
  };

  // ---- Lesson handlers ----
  const addLesson = () => {
    const newLesson = {
      id: tempId('lsn'),
      order: (course.lessons || []).length,
      title: `Lesson ${(course.lessons || []).length + 1}`,
      contentBlocks: [],
    };
    updateLocal({ lessons: [...(course.lessons || []), newLesson] });
    setActiveLessonId(newLesson.id);
  };

  const deleteLesson = (lessonId) => {
    if (!window.confirm('Delete this lesson? Its content blocks will be removed.')) return;
    const next = (course.lessons || []).filter((l) => l.id !== lessonId).map((l, i) => ({ ...l, order: i }));
    updateLocal({ lessons: next });
    if (activeLessonId === lessonId) {
      setActiveLessonId(next[0]?.id || null);
    }
  };

  const moveLesson = (lessonId, direction) => {
    const list = [...(course.lessons || [])];
    const idx = list.findIndex((l) => l.id === lessonId);
    if (idx < 0) return;
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= list.length) return;
    [list[idx], list[swapWith]] = [list[swapWith], list[idx]];
    updateLocal({ lessons: list.map((l, i) => ({ ...l, order: i })) });
  };

  const patchLesson = (lessonId, patch) => {
    updateLessons((l) => (l.id === lessonId ? { ...l, ...patch } : l));
  };

  const addBlock = (lessonId, type) => {
    updateLessons((l) => {
      if (l.id !== lessonId) return l;
      const newBlock = { id: tempId('blk'), type, text: '' };
      if (type === 'callout') newBlock.variant = 'info';
      return { ...l, contentBlocks: [...(l.contentBlocks || []), newBlock] };
    });
  };

  const patchBlock = (lessonId, blockId, patch) => {
    updateLessons((l) => {
      if (l.id !== lessonId) return l;
      return {
        ...l,
        contentBlocks: (l.contentBlocks || []).map((b) => (b.id === blockId ? { ...b, ...patch } : b)),
      };
    });
  };

  const deleteBlock = (lessonId, blockId) => {
    updateLessons((l) => {
      if (l.id !== lessonId) return l;
      return { ...l, contentBlocks: (l.contentBlocks || []).filter((b) => b.id !== blockId) };
    });
  };

  const moveBlock = (lessonId, blockId, direction) => {
    updateLessons((l) => {
      if (l.id !== lessonId) return l;
      const blocks = [...(l.contentBlocks || [])];
      const idx = blocks.findIndex((b) => b.id === blockId);
      if (idx < 0) return l;
      const swapWith = direction === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= blocks.length) return l;
      [blocks[idx], blocks[swapWith]] = [blocks[swapWith], blocks[idx]];
      return { ...l, contentBlocks: blocks };
    });
  };

  // ---- Quiz handlers ----
  const addQuestion = (type) => {
    const baseQ = { id: tempId('q'), type, prompt: '', explanation: '' };
    if (type === 'multiple_choice') {
      baseQ.options = ['', '', '', ''];
      baseQ.correctIndex = 0;
    } else {
      baseQ.correctBool = true;
    }
    const next = [...((course.quiz || {}).questions || []), baseQ];
    updateQuiz({ questions: next });
  };

  const patchQuestion = (qid, patch) => {
    const next = ((course.quiz || {}).questions || []).map((q) => (q.id === qid ? { ...q, ...patch } : q));
    updateQuiz({ questions: next });
  };

  const deleteQuestion = (qid) => {
    if (!window.confirm('Delete this question?')) return;
    const next = ((course.quiz || {}).questions || []).filter((q) => q.id !== qid);
    updateQuiz({ questions: next });
  };

  const moveQuestion = (qid, direction) => {
    const list = [...((course.quiz || {}).questions || [])];
    const idx = list.findIndex((q) => q.id === qid);
    if (idx < 0) return;
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= list.length) return;
    [list[idx], list[swapWith]] = [list[swapWith], list[idx]];
    updateQuiz({ questions: list });
  };

  const activeLesson = useMemo(() => {
    if (!course) return null;
    return (course.lessons || []).find((l) => l.id === activeLessonId) || null;
  }, [course, activeLessonId]);

  const validationIssues = useMemo(() => {
    if (!course) return [];
    const issues = [];
    if (!(course.lessons || []).length) issues.push('Add at least one lesson');
    const qs = (course.quiz || {}).questions || [];
    if (!qs.length) issues.push('Add at least one quiz question');
    qs.forEach((q, i) => {
      if (!(q.prompt || '').trim()) issues.push(`Question ${i + 1}: prompt is required`);
      if (q.type === 'multiple_choice') {
        const opts = (q.options || []).filter((o) => (o || '').trim());
        if (opts.length < 2) issues.push(`Question ${i + 1}: at least 2 non-empty options`);
        if (q.correctIndex == null || q.correctIndex < 0 || q.correctIndex >= (q.options || []).length) {
          issues.push(`Question ${i + 1}: pick a correct option`);
        }
      }
    });
    const pp = (course.quiz || {}).passPercent;
    if (!pp || pp < 1 || pp > 100) issues.push('Quiz pass percent must be 1–100');
    return issues;
  }, [course]);

  if (loading || !course) {
    return (
      <div data-testid="course-builder-loading" style={S.card}>
        <div style={S.heading}>Course Builder</div>
        <div style={{ color: C.textMid }}>Loading course...</div>
      </div>
    );
  }

  return (
    <div data-testid="course-builder" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header / actions */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="course-builder-back"
            onClick={() => {
              if (dirty && !window.confirm('You have unsaved changes. Discard and go back?')) return;
              onBack && onBack();
            }}
            style={{ ...S.buttonSecondary, padding: '8px 14px' }}
          >
            <Icon name="arrow-back-outline" style={{ fontSize: 14, marginRight: 4, verticalAlign: '-1px' }} />
            All courses
          </button>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {AUDIENCE_OPTIONS.find((o) => o.value === course.audience)?.label || course.audience}
              {course.mustCompleteBeforeAccess ? ' · Mandatory before access' : ''}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              {course.title}{' '}
              <span style={{ fontSize: 12, fontWeight: 600, color: C.textLight, marginLeft: 6 }}>
                {course.status === 'published' ? `Published · v${course.version}` : (course.status || 'draft').toUpperCase()}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="course-save-btn"
              disabled={!dirty || saving}
              onClick={handleSave}
              style={{ ...S.buttonSecondary, opacity: (!dirty || saving) ? 0.5 : 1 }}
            >
              {saving ? 'Saving...' : (dirty ? 'Save Changes' : 'Saved')}
            </button>
            {course.status === 'published' ? (
              <button
                type="button"
                data-testid="course-unpublish-btn"
                onClick={handleUnpublish}
                style={S.buttonSecondary}
              >
                Move to Draft
              </button>
            ) : (
              <button
                type="button"
                data-testid="course-publish-btn"
                disabled={publishing || validationIssues.length > 0}
                title={validationIssues.length ? `Fix: ${validationIssues.join(' · ')}` : ''}
                onClick={handlePublish}
                style={{ ...S.buttonPrimary, opacity: (publishing || validationIssues.length > 0) ? 0.5 : 1 }}
              >
                {publishing ? 'Publishing...' : 'Publish'}
              </button>
            )}
          </div>
        </div>

        {validationIssues.length > 0 && (
          <div
            data-testid="course-validation-issues"
            style={{ marginTop: 12, padding: 10, borderRadius: 8, background: C.orangeLight, border: `1px solid ${C.orange}33`, color: C.orange, fontSize: 12 }}
          >
            <strong>Before publishing:</strong> {validationIssues.join(' · ')}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginTop: 16, borderBottom: `1px solid ${C.border}` }}>
          {[
            { id: 'metadata', label: 'Metadata', icon: 'information-circle-outline' },
            { id: 'lessons',  label: `Lessons (${(course.lessons || []).length})`, icon: 'list-outline' },
            { id: 'quiz',     label: `Quiz (${((course.quiz || {}).questions || []).length})`, icon: 'help-buoy-outline' },
            { id: 'preview',  label: 'Preview', icon: 'eye-outline' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === t.id ? `2px solid ${C.accent}` : '2px solid transparent',
                color: activeTab === t.id ? C.accent : C.textMid,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'Inter', sans-serif",
                transition: 'color 0.15s',
              }}
            >
              <Icon name={t.icon} style={{ fontSize: 14, marginRight: 6, verticalAlign: '-2px' }} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'metadata' && (
        <MetadataPanel course={course} onChange={updateLocal} />
      )}

      {activeTab === 'lessons' && (
        <LessonsPanel
          course={course}
          activeLessonId={activeLessonId}
          setActiveLessonId={setActiveLessonId}
          activeLesson={activeLesson}
          onAddLesson={addLesson}
          onDeleteLesson={deleteLesson}
          onMoveLesson={moveLesson}
          onPatchLesson={patchLesson}
          onAddBlock={addBlock}
          onPatchBlock={patchBlock}
          onDeleteBlock={deleteBlock}
          onMoveBlock={moveBlock}
        />
      )}

      {activeTab === 'quiz' && (
        <QuizPanel
          course={course}
          onUpdateQuiz={updateQuiz}
          onAddQuestion={addQuestion}
          onPatchQuestion={patchQuestion}
          onDeleteQuestion={deleteQuestion}
          onMoveQuestion={moveQuestion}
        />
      )}

      {activeTab === 'preview' && <PreviewPanel course={course} />}
    </div>
  );
}

// ============================================================================
// Sub-panels
// ============================================================================

function MetadataPanel({ course, onChange }) {
  return (
    <div style={S.card} data-testid="metadata-panel">
      <div style={S.heading}>Course Metadata</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>Title *</label>
          <input
            data-testid="meta-title"
            type="text"
            value={course.title || ''}
            onChange={(e) => onChange({ title: e.target.value })}
            style={S.input}
          />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={S.label}>Description</label>
          <textarea
            data-testid="meta-description"
            value={course.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={3}
            style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 500, resize: 'vertical' }}
          />
        </div>
        <div>
          <label style={S.label}>Audience *</label>
          <select
            data-testid="meta-audience"
            value={course.audience}
            onChange={(e) => onChange({ audience: e.target.value })}
            style={S.select}
          >
            {AUDIENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Estimated Minutes</label>
          <input
            data-testid="meta-minutes"
            type="number"
            min="1"
            value={course.estimatedMinutes ?? ''}
            onChange={(e) => onChange({ estimatedMinutes: e.target.value === '' ? null : Number(e.target.value) })}
            style={S.input}
            placeholder="e.g. 15"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <label
            data-testid="meta-must-complete-label"
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMid, fontWeight: 500, cursor: 'pointer' }}
          >
            <input
              data-testid="meta-must-complete"
              type="checkbox"
              checked={!!course.mustCompleteBeforeAccess}
              onChange={(e) => onChange({ mustCompleteBeforeAccess: e.target.checked })}
            />
            Must complete before portal access (gate)
          </label>
        </div>
      </div>
      <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: C.bg, border: `1px solid ${C.border}`, fontSize: 12, color: C.textLight, lineHeight: 1.5 }}>
        Course id: <code>{course.id}</code> · Created {new Date(course.createdAt).toLocaleString()}{' '}
        by {course.createdBy?.name || '—'}
      </div>
    </div>
  );
}

function LessonsPanel({
  course, activeLessonId, setActiveLessonId, activeLesson,
  onAddLesson, onDeleteLesson, onMoveLesson, onPatchLesson,
  onAddBlock, onPatchBlock, onDeleteBlock, onMoveBlock,
}) {
  const lessons = course.lessons || [];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }} data-testid="lessons-panel">
      {/* Lessons sidebar */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ ...S.heading, margin: 0 }}>Lessons</div>
          <button
            type="button"
            data-testid="add-lesson-btn"
            onClick={onAddLesson}
            style={{ ...S.buttonPrimary, padding: '6px 10px', fontSize: 12 }}
          >
            + Add
          </button>
        </div>
        {lessons.length === 0 && (
          <div style={{ color: C.textLight, fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>
            No lessons yet. Click <strong>+ Add</strong> to start.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {lessons.map((l, idx) => (
            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                type="button"
                data-testid={`lesson-tab-${l.id}`}
                onClick={() => setActiveLessonId(l.id)}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: activeLessonId === l.id ? C.accentBg : 'transparent',
                  color: activeLessonId === l.id ? C.accent : C.text,
                  fontSize: 13,
                  fontWeight: activeLessonId === l.id ? 600 : 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                <span style={{ fontSize: 11, color: C.textLight, fontFamily: "'JetBrains Mono', monospace" }}>{idx + 1}.</span>{' '}
                {l.title || `Lesson ${idx + 1}`}
              </button>
              <button
                type="button"
                data-testid={`lesson-up-${l.id}`}
                disabled={idx === 0}
                onClick={() => onMoveLesson(l.id, 'up')}
                title="Move up"
                style={{ ...iconBtnStyle, opacity: idx === 0 ? 0.3 : 1 }}
              >▲</button>
              <button
                type="button"
                data-testid={`lesson-down-${l.id}`}
                disabled={idx === lessons.length - 1}
                onClick={() => onMoveLesson(l.id, 'down')}
                title="Move down"
                style={{ ...iconBtnStyle, opacity: idx === lessons.length - 1 ? 0.3 : 1 }}
              >▼</button>
            </div>
          ))}
        </div>
      </div>

      {/* Active lesson editor */}
      <div style={S.card}>
        {!activeLesson && (
          <div style={{ color: C.textLight, fontStyle: 'italic' }}>
            Select a lesson on the left, or add one to begin.
          </div>
        )}
        {activeLesson && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={S.label}>Lesson Title</label>
                <input
                  data-testid="lesson-title"
                  type="text"
                  value={activeLesson.title || ''}
                  onChange={(e) => onPatchLesson(activeLesson.id, { title: e.target.value })}
                  style={S.input}
                />
              </div>
              <div style={{ flex: '0 0 140px' }}>
                <label style={S.label}>Est. Minutes</label>
                <input
                  data-testid="lesson-minutes"
                  type="number"
                  min="0"
                  value={activeLesson.estimatedMinutes ?? ''}
                  onChange={(e) => onPatchLesson(activeLesson.id, { estimatedMinutes: e.target.value === '' ? null : Number(e.target.value) })}
                  style={S.input}
                />
              </div>
              <button
                type="button"
                data-testid="lesson-delete"
                onClick={() => onDeleteLesson(activeLesson.id)}
                style={{ ...S.buttonDanger, padding: '8px 12px', fontSize: 12, alignSelf: 'flex-end' }}
              >
                Delete Lesson
              </button>
            </div>

            {/* Add block toolbar */}
            <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: 12 }}>
              <div style={{ ...S.heading, margin: '0 0 8px' }}>Content Blocks</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {BLOCK_TYPES.map((bt) => (
                  <button
                    key={bt.value}
                    type="button"
                    data-testid={`add-block-${bt.value}`}
                    onClick={() => onAddBlock(activeLesson.id, bt.value)}
                    style={{ ...S.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
                  >
                    <Icon name={bt.icon} style={{ fontSize: 12, marginRight: 4, verticalAlign: '-1px' }} />
                    {bt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Blocks */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(activeLesson.contentBlocks || []).length === 0 && (
                <div style={{ padding: 18, border: `1px dashed ${C.border}`, borderRadius: 8, textAlign: 'center', color: C.textLight, fontSize: 13 }}>
                  No content blocks yet. Use the toolbar above to add one.
                </div>
              )}
              {(activeLesson.contentBlocks || []).map((block, idx, arr) => (
                <BlockEditor
                  key={block.id}
                  block={block}
                  index={idx}
                  total={arr.length}
                  onPatch={(patch) => onPatchBlock(activeLesson.id, block.id, patch)}
                  onDelete={() => onDeleteBlock(activeLesson.id, block.id)}
                  onMove={(dir) => onMoveBlock(activeLesson.id, block.id, dir)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const iconBtnStyle = {
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  width: 22,
  height: 22,
  fontSize: 10,
  cursor: 'pointer',
  color: C.textMid,
};

function BlockEditor({ block, index, total, onPatch, onDelete, onMove }) {
  const meta = BLOCK_TYPES.find((b) => b.value === block.type) || { label: block.type, icon: 'cube-outline' };
  return (
    <div
      data-testid={`block-${block.id}`}
      style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.card }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${C.borderLight}`, background: C.bg, borderRadius: '10px 10px 0 0' }}>
        <Icon name={meta.icon} style={{ fontSize: 14, color: C.textMid }} />
        <span style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {meta.label} · #{index + 1}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" data-testid={`block-up-${block.id}`} disabled={index === 0} onClick={() => onMove('up')} style={{ ...iconBtnStyle, opacity: index === 0 ? 0.3 : 1 }}>▲</button>
        <button type="button" data-testid={`block-down-${block.id}`} disabled={index === total - 1} onClick={() => onMove('down')} style={{ ...iconBtnStyle, opacity: index === total - 1 ? 0.3 : 1 }}>▼</button>
        <button type="button" data-testid={`block-delete-${block.id}`} onClick={onDelete} style={{ ...S.buttonDanger, padding: '4px 10px', fontSize: 11 }}>Delete</button>
      </div>
      <div style={{ padding: 12 }}>
        <BlockBody block={block} onPatch={onPatch} />
      </div>
    </div>
  );
}

function BlockBody({ block, onPatch }) {
  const ta = (placeholder, key = 'text', rows = 4) => (
    <textarea
      data-testid={`block-${block.id}-${key}`}
      value={block[key] || ''}
      onChange={(e) => onPatch({ [key]: e.target.value })}
      rows={rows}
      placeholder={placeholder}
      style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 500, resize: 'vertical' }}
    />
  );
  const inp = (placeholder, key) => (
    <input
      data-testid={`block-${block.id}-${key}`}
      type="text"
      value={block[key] || ''}
      onChange={(e) => onPatch({ [key]: e.target.value })}
      placeholder={placeholder}
      style={S.input}
    />
  );

  switch (block.type) {
    case 'richText':
      return (
        <div>
          {ta('Markdown content. Use # for headings, **bold**, lists, links, etc.', 'text', 6)}
          {block.text ? (
            <div style={previewBoxStyle}>
              <div style={previewLabelStyle}>Live Preview</div>
              <div className="md-preview" dangerouslySetInnerHTML={{ __html: renderMarkdown(block.text) }} />
            </div>
          ) : null}
        </div>
      );
    case 'image':
      return (
        <div style={{ display: 'grid', gap: 8 }}>
          {inp('https://...', 'url')}
          {inp('Alt text', 'alt')}
          {block.url && (
            <img
              src={block.url}
              alt={block.alt || ''}
              style={{ maxWidth: '100%', borderRadius: 8, border: `1px solid ${C.border}` }}
            />
          )}
        </div>
      );
    case 'videoEmbed':
      return (
        <div style={{ display: 'grid', gap: 8 }}>
          {inp('YouTube or Vimeo URL', 'url')}
          {inp('Optional caption', 'text')}
          <div style={{ fontSize: 11, color: C.textLight }}>Player rendered at runtime in Phase 18.2.</div>
        </div>
      );
    case 'attachment':
      return (
        <div style={{ display: 'grid', gap: 8 }}>
          {inp('https://... (PDF / link)', 'url')}
          {inp('Display name (e.g. "AM Cheat Sheet.pdf")', 'name')}
        </div>
      );
    case 'callout':
      return (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 160px' }}>
              <label style={S.label}>Variant</label>
              <select
                data-testid={`block-${block.id}-variant`}
                value={block.variant || 'info'}
                onChange={(e) => onPatch({ variant: e.target.value })}
                style={S.select}
              >
                {CALLOUT_VARIANTS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Title (optional)</label>
              {inp('e.g. Heads up', 'title')}
            </div>
          </div>
          {ta('Body of the callout (supports markdown).', 'text', 3)}
        </div>
      );
    case 'keyTakeaway':
      return ta('A single sentence the learner must remember.', 'text', 2);
    case 'definitionTooltip':
      return (
        <div style={{ display: 'grid', gap: 8 }}>
          {inp('Term', 'term')}
          {ta('Definition', 'definition', 2)}
        </div>
      );
    case 'reflectionPrompt':
      return ta('A non-graded reflection question to deepen understanding.', 'text', 2);
    default:
      return ta('Content', 'text', 3);
  }
}

const previewBoxStyle = {
  marginTop: 8,
  padding: 10,
  borderRadius: 8,
  background: C.bg,
  border: `1px dashed ${C.border}`,
};
const previewLabelStyle = {
  fontSize: 10,
  color: C.textLight,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 6,
};

function QuizPanel({ course, onUpdateQuiz, onAddQuestion, onPatchQuestion, onDeleteQuestion, onMoveQuestion }) {
  const quiz = course.quiz || {};
  const questions = quiz.questions || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="quiz-panel">
      {/* Quiz settings */}
      <div style={S.card}>
        <div style={S.heading}>Quiz Settings</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label style={S.label}>Pass % *</label>
            <input
              data-testid="quiz-pass-percent"
              type="number"
              min="1"
              max="100"
              value={quiz.passPercent ?? 80}
              onChange={(e) => onUpdateQuiz({ passPercent: Number(e.target.value) || 0 })}
              style={S.input}
            />
          </div>
          <div>
            <label style={S.label}>Max Attempts</label>
            <input
              data-testid="quiz-max-attempts"
              type="number"
              min="1"
              value={quiz.maxAttempts ?? ''}
              onChange={(e) => onUpdateQuiz({ maxAttempts: e.target.value === '' ? null : Number(e.target.value) })}
              placeholder="Unlimited"
              style={S.input}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMid, fontWeight: 500, cursor: 'pointer' }}>
              <input
                data-testid="quiz-randomize"
                type="checkbox"
                checked={!!quiz.randomizeQuestions}
                onChange={(e) => onUpdateQuiz({ randomizeQuestions: e.target.checked })}
              />
              Randomize question order
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMid, fontWeight: 500, cursor: 'pointer' }}>
              <input
                data-testid="quiz-review"
                type="checkbox"
                checked={quiz.reviewAnswers !== false}
                onChange={(e) => onUpdateQuiz({ reviewAnswers: e.target.checked })}
              />
              Allow learners to review answers
            </label>
          </div>
        </div>
      </div>

      {/* Questions list */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ ...S.heading, margin: 0 }}>Questions ({questions.length})</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              data-testid="add-mc-question"
              onClick={() => onAddQuestion('multiple_choice')}
              style={{ ...S.buttonPrimary, padding: '6px 10px', fontSize: 12 }}
            >
              + Multiple Choice
            </button>
            <button
              type="button"
              data-testid="add-tf-question"
              onClick={() => onAddQuestion('true_false')}
              style={{ ...S.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
            >
              + True/False
            </button>
          </div>
        </div>
        {questions.length === 0 && (
          <div style={{ padding: 18, border: `1px dashed ${C.border}`, borderRadius: 8, textAlign: 'center', color: C.textLight, fontSize: 13 }}>
            No questions yet. Add at least one to publish.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {questions.map((q, idx) => (
            <QuestionEditor
              key={q.id}
              q={q}
              index={idx}
              total={questions.length}
              onPatch={(patch) => onPatchQuestion(q.id, patch)}
              onDelete={() => onDeleteQuestion(q.id)}
              onMove={(dir) => onMoveQuestion(q.id, dir)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function QuestionEditor({ q, index, total, onPatch, onDelete, onMove }) {
  const isMC = q.type === 'multiple_choice';
  return (
    <div
      data-testid={`question-${q.id}`}
      style={{ border: `1px solid ${C.border}`, borderRadius: 10 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${C.borderLight}`, background: C.bg, borderRadius: '10px 10px 0 0' }}>
        <span style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Q{index + 1} · {isMC ? 'Multiple Choice' : 'True / False'}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" data-testid={`question-up-${q.id}`} disabled={index === 0} onClick={() => onMove('up')} style={{ ...iconBtnStyle, opacity: index === 0 ? 0.3 : 1 }}>▲</button>
        <button type="button" data-testid={`question-down-${q.id}`} disabled={index === total - 1} onClick={() => onMove('down')} style={{ ...iconBtnStyle, opacity: index === total - 1 ? 0.3 : 1 }}>▼</button>
        <button type="button" data-testid={`question-delete-${q.id}`} onClick={onDelete} style={{ ...S.buttonDanger, padding: '4px 10px', fontSize: 11 }}>Delete</button>
      </div>
      <div style={{ padding: 12, display: 'grid', gap: 10 }}>
        <div>
          <label style={S.label}>Prompt *</label>
          <textarea
            data-testid={`question-${q.id}-prompt`}
            value={q.prompt || ''}
            onChange={(e) => onPatch({ prompt: e.target.value })}
            rows={2}
            style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 500, resize: 'vertical' }}
          />
        </div>

        {isMC && (
          <div>
            <label style={S.label}>Options (select the correct one)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(q.options || []).map((opt, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    data-testid={`question-${q.id}-correct-${i}`}
                    name={`correct-${q.id}`}
                    checked={q.correctIndex === i}
                    onChange={() => onPatch({ correctIndex: i })}
                  />
                  <input
                    type="text"
                    data-testid={`question-${q.id}-option-${i}`}
                    value={opt}
                    onChange={(e) => {
                      const next = [...(q.options || [])];
                      next[i] = e.target.value;
                      onPatch({ options: next });
                    }}
                    placeholder={`Option ${i + 1}`}
                    style={{ ...S.input, flex: 1 }}
                  />
                  <button
                    type="button"
                    data-testid={`question-${q.id}-remove-option-${i}`}
                    onClick={() => {
                      const next = (q.options || []).filter((_, j) => j !== i);
                      let ci = q.correctIndex;
                      if (ci === i) ci = 0;
                      else if (ci > i) ci -= 1;
                      onPatch({ options: next, correctIndex: ci });
                    }}
                    disabled={(q.options || []).length <= 2}
                    style={{ ...S.buttonSecondary, padding: '6px 10px', fontSize: 11, opacity: (q.options || []).length <= 2 ? 0.3 : 1 }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid={`question-${q.id}-add-option`}
              onClick={() => onPatch({ options: [...(q.options || []), ''] })}
              style={{ ...S.buttonSecondary, padding: '6px 10px', fontSize: 12, marginTop: 8 }}
            >
              + Add Option
            </button>
          </div>
        )}

        {!isMC && (
          <div>
            <label style={S.label}>Correct Answer</label>
            <div style={{ display: 'flex', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.textMid, cursor: 'pointer' }}>
                <input
                  type="radio"
                  data-testid={`question-${q.id}-correct-true`}
                  name={`tf-${q.id}`}
                  checked={q.correctBool === true}
                  onChange={() => onPatch({ correctBool: true })}
                />
                True
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.textMid, cursor: 'pointer' }}>
                <input
                  type="radio"
                  data-testid={`question-${q.id}-correct-false`}
                  name={`tf-${q.id}`}
                  checked={q.correctBool === false}
                  onChange={() => onPatch({ correctBool: false })}
                />
                False
              </label>
            </div>
          </div>
        )}

        <div>
          <label style={S.label}>Explanation (shown when reviewing answers)</label>
          <textarea
            data-testid={`question-${q.id}-explanation`}
            value={q.explanation || ''}
            onChange={(e) => onPatch({ explanation: e.target.value })}
            rows={2}
            style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 500, resize: 'vertical' }}
          />
        </div>
      </div>
    </div>
  );
}

function PreviewPanel({ course }) {
  return (
    <div style={S.card} data-testid="preview-panel">
      <div style={{ ...S.heading, marginBottom: 4 }}>Learner Preview</div>
      <div style={{ color: C.textLight, fontSize: 12, marginBottom: 16 }}>
        Approximate runtime experience. Actual portal layout ships in Phase 18.2.
      </div>

      <div style={{ borderLeft: `4px solid ${C.accent}`, paddingLeft: 14, marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 24, color: C.text }}>{course.title}</h2>
        {course.description && (
          <p style={{ margin: '4px 0 0', color: C.textMid, fontSize: 14 }}>{course.description}</p>
        )}
        <div style={{ marginTop: 6, fontSize: 12, color: C.textLight }}>
          Audience: {AUDIENCE_OPTIONS.find((o) => o.value === course.audience)?.label || course.audience}
          {course.estimatedMinutes ? ` · ~${course.estimatedMinutes} min` : ''}
          {course.mustCompleteBeforeAccess ? ' · Required for portal access' : ''}
        </div>
      </div>

      {(course.lessons || []).map((lesson, idx) => (
        <div key={lesson.id} style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, color: C.text, margin: '0 0 8px', borderBottom: `1px solid ${C.borderLight}`, paddingBottom: 6 }}>
            Lesson {idx + 1}: {lesson.title}
          </h3>
          {(lesson.contentBlocks || []).map((b) => <PreviewBlock key={b.id} block={b} />)}
          {(lesson.contentBlocks || []).length === 0 && (
            <div style={{ color: C.textLight, fontStyle: 'italic', fontSize: 13 }}>(empty lesson)</div>
          )}
        </div>
      ))}

      {(course.lessons || []).length === 0 && (
        <div style={{ color: C.textLight, fontStyle: 'italic', fontSize: 13 }}>No lessons yet.</div>
      )}

      {/* Quiz preview */}
      <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: 16, marginTop: 8 }}>
        <h3 style={{ fontSize: 16, color: C.text, margin: '0 0 6px' }}>Final Quiz</h3>
        <div style={{ fontSize: 12, color: C.textLight, marginBottom: 12 }}>
          Pass: {(course.quiz || {}).passPercent ?? 80}% · Attempts:{' '}
          {(course.quiz || {}).maxAttempts ?? 'Unlimited'}{' '}
          {course.quiz?.randomizeQuestions ? '· Randomized order' : ''}
        </div>
        {((course.quiz || {}).questions || []).map((q, i) => (
          <div key={q.id} style={{ marginBottom: 14, padding: 12, border: `1px solid ${C.border}`, borderRadius: 8 }}>
            <div style={{ fontWeight: 600, color: C.text, marginBottom: 8 }}>
              Q{i + 1}. {q.prompt || <em style={{ color: C.textLight }}>(empty prompt)</em>}
            </div>
            {q.type === 'multiple_choice' ? (
              <ul style={{ margin: 0, paddingLeft: 18, color: C.textMid, fontSize: 13 }}>
                {(q.options || []).map((o, j) => (
                  <li key={j} style={{ color: q.correctIndex === j ? C.green : C.textMid, fontWeight: q.correctIndex === j ? 600 : 400 }}>
                    {o || <em style={{ color: C.textLight }}>(empty)</em>}
                    {q.correctIndex === j ? '  ✓' : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: C.textMid, fontSize: 13 }}>
                Correct answer:{' '}
                <strong style={{ color: C.green }}>{q.correctBool ? 'True' : 'False'}</strong>
              </div>
            )}
            {q.explanation && (
              <div style={{ marginTop: 8, padding: 8, background: C.bg, borderRadius: 6, fontSize: 12, color: C.textMid }}>
                <strong>Why:</strong> {q.explanation}
              </div>
            )}
          </div>
        ))}
        {((course.quiz || {}).questions || []).length === 0 && (
          <div style={{ color: C.textLight, fontStyle: 'italic', fontSize: 13 }}>No questions yet.</div>
        )}
      </div>
    </div>
  );
}

function PreviewBlock({ block }) {
  switch (block.type) {
    case 'richText':
      return (
        <div className="md-preview" style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(block.text || '') }} />
      );
    case 'image':
      return block.url ? (
        <img src={block.url} alt={block.alt || ''} style={{ maxWidth: '100%', borderRadius: 8, margin: '8px 0' }} />
      ) : <em style={{ color: C.textLight }}>(image — no URL set)</em>;
    case 'videoEmbed':
      return (
        <div style={{ padding: 12, background: C.bg, border: `1px dashed ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textMid, margin: '8px 0' }}>
          🎬 Video: <code>{block.url || '(no URL)'}</code>
          {block.text ? <div style={{ marginTop: 4, fontSize: 12 }}>{block.text}</div> : null}
        </div>
      );
    case 'attachment':
      return (
        <div style={{ margin: '8px 0', fontSize: 13 }}>
          📎 <a href={block.url || '#'} target="_blank" rel="noreferrer" style={{ color: C.accent }}>
            {block.name || block.url || '(attachment)'}
          </a>
        </div>
      );
    case 'callout': {
      const v = CALLOUT_VARIANTS.find((cv) => cv.value === (block.variant || 'info')) || CALLOUT_VARIANTS[0];
      return (
        <div style={{ padding: 12, borderRadius: 8, background: v.bg, border: `1px solid ${v.color}33`, color: v.color, margin: '8px 0' }}>
          {block.title && <div style={{ fontWeight: 700, marginBottom: 4 }}>{block.title}</div>}
          <div className="md-preview" style={{ color: C.text, fontSize: 13, lineHeight: 1.5 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(block.text || '') }} />
        </div>
      );
    }
    case 'keyTakeaway':
      return (
        <div style={{ padding: 12, borderRadius: 8, background: C.accentBg, borderLeft: `4px solid ${C.accent}`, margin: '8px 0' }}>
          <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Key Takeaway</div>
          <div style={{ color: C.text, fontSize: 14, marginTop: 4 }}>{block.text || <em style={{ color: C.textLight }}>(empty)</em>}</div>
        </div>
      );
    case 'definitionTooltip':
      return (
        <div style={{ margin: '8px 0', fontSize: 13 }}>
          <strong style={{ color: C.text }}>{block.term || '(term)'}:</strong>{' '}
          <span style={{ color: C.textMid }}>{block.definition || '(definition)'}</span>
        </div>
      );
    case 'reflectionPrompt':
      return (
        <div style={{ padding: 12, borderRadius: 8, background: C.purpleLight, borderLeft: `4px solid ${C.purple}`, margin: '8px 0' }}>
          <div style={{ fontSize: 11, color: C.purple, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Reflection</div>
          <div style={{ color: C.text, fontSize: 14, marginTop: 4 }}>{block.text || <em style={{ color: C.textLight }}>(empty)</em>}</div>
        </div>
      );
    default:
      return null;
  }
}
